import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import moment from 'moment';
import 'react-dates/initialize';
import { DayPickerRangeController } from 'react-dates';

import { IconArrowRight } from '@lightspeed/flame/Icon/ArrowRight';
import { IconArrowLeft } from '@lightspeed/flame/Icon/ArrowLeft';
import { IconCalendar } from '@lightspeed/flame/Icon/Calendar';
import { IconSmallChevronDown } from '@lightspeed/flame/Icon/SmallChevronDown';
import Text from '@lightspeed/cirrus-text';
import { Popover } from '@lightspeed/flame/Popover';
import { Spinner } from '@lightspeed/flame/Spinner';

import { changeDate, isToday, DEFAULT_DATE_FORMAT } from 'pages/utils/time';

import DateRangePickerPresets from './DateRangePickerPresets';
import DateRangePickerForm from './DateRangePickerForm';
import DatePicker from './DatePicker';
import { getValidDatesInRange, isOutsideRange, isPresetEqual, ValidateDate } from './utils';

import { Day } from './DateRangePicker.style';

const START_DATE = 'startDate';
const END_DATE = 'endDate';
const VISIBLE_MONTHS = 2;
const MINIMUM_NIGHTS = 0;
const TRANSITION_DURATION = 100;
const ARROW_LEFT = <IconArrowLeft />;
const ARROW_RIGHT = <IconArrowRight />;
const CALENDAR_MONTH_FORMAT = 'MMMM YYYY';
const CALENDAR_WEEKDAY_FORMAT = 'ddd';
const CALENDAR_DAY_FORMAT = 'D';

class DateRangePicker extends Component {
  constructor(props) {
    super(props);

    this.state = this.getInitialState({ props, isOpen: false });

    this.handleDatesChange = this.handleDatesChange.bind(this);
    this.handleStartDateFocus = this.handleStartDateFocus.bind(this);
    this.handleEndDateFocus = this.handleEndDateFocus.bind(this);
    this.handleStartDateBlur = this.handleStartDateBlur.bind(this);
    this.handleEndDateBlur = this.handleEndDateBlur.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleOpen = this.handleOpen.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.renderButton = this.renderButton.bind(this);
    this.renderMonthElement = this.renderMonthElement.bind(this);
    this.renderDayContents = this.renderDayContents.bind(this);
  }

  getInitialState({ props, isOpen }) {
    const { startDate, endDate, initialVisibleMonth } = props;

    return {
      key: 1,
      startDate: props.startDate,
      endDate: props.endDate,
      isOpen,
      focusedInput: START_DATE,
      initialVisibleMonth,
      selectedPreset: this.getSelectedPreset({ startDate, endDate }),
    };
  }

  componentDidUpdate(prevProps) {
    const { presetDates, startDate, endDate } = this.props;
    if (
      prevProps.startDate.format() !== startDate.format() ||
      prevProps.endDate.format() !== endDate.format() ||
      ((prevProps.presetDates || presetDates) &&
        !isPresetEqual({ preset: prevProps.presetDates, otherPreset: presetDates }))
    ) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(prevState =>
        this.getInitialState({ props: this.props, isOpen: prevState.isOpen })
      );
    }
  }

  getSelectedPreset({ startDate, endDate }) {
    const { presetDates } = this.props;
    const selectedPreset =
      presetDates &&
      presetDates.find(
        preset => preset.startDate.isSame(startDate, 'day') && preset.endDate.isSame(endDate, 'day')
      );

    return selectedPreset && selectedPreset !== -1 ? selectedPreset.label : '';
  }

  handleStartDateFocus() {
    this.setState(() => ({
      focusedInput: START_DATE,
    }));
  }

  handleEndDateFocus() {
    this.setState(() => ({
      focusedInput: END_DATE,
    }));
  }

  handleStartDateBlur(event) {
    const { target: { value } = {} } = event;
    const { endDate } = this.state;
    const {
      availableStartDate,
      availableEndDate,
      dateFormat,
      single,
      offset,
      customValidation,
    } = this.props;
    const newStartDate = moment(value, dateFormat);

    /**
     * Since onBlur triggers before formik's validation we need to re-validate before changing it.
     */

    const startDateError = ValidateDate({
      name: START_DATE,
      value,
      oppositeValue: endDate,
      availableStartDate,
      availableEndDate,
      dateFormat,
      single,
      offset,
      customValidation,
    });

    if (!startDateError) {
      if (moment(newStartDate).isAfter(moment(endDate).endOf('day'))) {
        this.handleDatesChange({
          startDate: newStartDate,
          endDate: newStartDate,
          forceRender: true,
          focusedInput: single ? START_DATE : END_DATE,
        });
        return;
      }

      this.handleDatesChange({
        startDate: newStartDate,
        endDate,
        forceRender: true,
        focusedInput: single ? START_DATE : END_DATE,
      });
    }
  }

  handleEndDateBlur(event) {
    const { target: { value } = {} } = event;
    const { startDate } = this.state;
    const {
      availableStartDate,
      availableEndDate,
      dateFormat,
      single,
      offset,
      customValidation,
    } = this.props;
    const newEndDate = moment(value, dateFormat);

    /**
     * Since onBlur triggers before formik's validation we need to re-validate before changing it.
     */

    const endDateError = ValidateDate({
      name: END_DATE,
      value,
      oppositeValue: startDate,
      availableStartDate,
      availableEndDate,
      dateFormat,
      single,
      offset,
      customValidation,
    });

    if (!endDateError) {
      if (moment(newEndDate).isBefore(moment(startDate).startOf('day'))) {
        this.handleDatesChange({
          startDate: newEndDate,
          endDate: newEndDate,
          forceRender: true,
          focusedInput: START_DATE,
        });
        return;
      }

      this.handleDatesChange({
        startDate,
        endDate: newEndDate,
        forceRender: true,
        focusedInput: START_DATE,
      });
    }
  }

  /**
   *
   * @param startDate {moment}
   * @param endDate {moment}
   * @param forceRender {Boolean} - force the calendar re-render and, if set, jump to the selected initialMonth
   * @param focusedInput {START_DATE|END_DATE} - the new focused input
   */

  handleDatesChange({ startDate, endDate, forceRender, focusedInput }) {
    const { single } = this.props;
    const { startDate: oldStartDate, endDate: oldEndDate } = this.state;
    let { key } = this.state;

    if (forceRender) {
      key += 1;
    }

    const newStartDate = changeDate({ oldDate: oldStartDate, newDate: startDate });
    const newEndDate = single
      ? newStartDate
      : changeDate({ oldDate: oldEndDate, newDate: endDate || startDate });
    if (!newStartDate.isSame(oldStartDate) || !newEndDate.isSame(oldEndDate)) {
      this.setState(() => ({
        key,
        startDate: newStartDate,
        endDate: newEndDate,
        focusedInput,
      }));
    }
  }

  handleOpen() {
    this.setState(this.getInitialState({ props: this.props, isOpen: true }));
  }

  handleClose() {
    this.setState({
      isOpen: false,
    });
  }

  handleSubmit(values) {
    const { onChange, dateFormat, single } = this.props;
    const { startDate, endDate } = this.state;

    /**
     * Using the form values on submit,
     * since submitting on ENTER KEY, the state update happens after the event is triggered
     */
    const startDateTime = changeDate({
      oldDate: startDate,
      newDate: moment(values[START_DATE], dateFormat),
    });
    const endDateTime = changeDate({
      oldDate: endDate,
      newDate: moment(values[END_DATE], dateFormat),
    });

    if (single) {
      onChange({
        date: startDateTime,
      });
    } else {
      onChange({
        startDate: startDateTime,
        endDate: endDateTime,
      });
    }

    this.handleClose();
  }

  renderButton({ targetProps, targetEvents, active, disabled }) {
    const { selectedPreset } = this.state;
    const { single, startDate, endDate, dateFormat } = this.props;
    const buttonCls = cn('cr-button', 'date-picker__button', {
      'date-picker__button--active': active,
    });

    return (
      <button
        type="button"
        className={buttonCls}
        {...targetProps}
        {...targetEvents}
        disabled={disabled}
      >
        <div key="calendar" className="cr-button__content">
          <IconCalendar />
        </div>
        {!selectedPreset && (
          <div key="start-date" className="cr-button__content">
            {startDate && <Text dimmed>{startDate.format(dateFormat)}</Text>}
          </div>
        )}
        {!selectedPreset && !single && (
          <div key="arrow" className="cr-button__content">
            <IconArrowRight />
          </div>
        )}
        {!selectedPreset && !single && (
          <div key="end-date" className="cr-button__content">
            {endDate && <Text dimmed>{endDate.format(dateFormat)}</Text>}
          </div>
        )}
        {selectedPreset && (
          <div key="selected-preset" className="cr-button__content">
            {selectedPreset}
          </div>
        )}
        <div key="arrow-down" className="cr-button__content">
          <IconSmallChevronDown />
        </div>
      </button>
    );
  }

  renderMonthElement({ month }) {
    const { availableStartDate, availableEndDate } = this.props;

    const { validStartDate, validEndDate } = getValidDatesInRange({
      startDate: moment(month).startOf('month'),
      endDate: moment(month).endOf('month'),
      rangeStartDate: availableStartDate,
      rangeEndDate: availableEndDate,
    });

    return (
      // eslint-disable-next-line
      <div
        onClick={() =>
          validStartDate &&
          validEndDate &&
          this.handleDatesChange({
            startDate: validStartDate,
            endDate: validEndDate,
            focusedInput: START_DATE,
          })
        }
        className="cr-date-picker__month-button cr-text--bold"
        role="button"
      >
        {month.format(CALENDAR_MONTH_FORMAT)}
      </div>
    );
  }

  renderDayContents(date) {
    const { offset } = this.props;
    return (
      <Day isToday={isToday({ date, now: moment(), offset })}>
        {moment(date).format(CALENDAR_DAY_FORMAT)}
      </Day>
    );
  }

  render() {
    const { key, startDate, endDate, isOpen, focusedInput } = this.state;
    const {
      initialVisibleMonth,
      availableStartDate,
      availableEndDate,
      presetDates,
      dateFormat,
      onChange,
      startDate: propsStartDate,
      endDate: propsEndDate,
      numberOfMonths,
      placement,
      single,
      disabled,
      offset,
      customValidation,
      ...rest
    } = this.props;

    return dateFormat ? (
      <Popover
        light
        target={({ targetProps, targetEvents, active }) =>
          this.renderButton({ targetProps, targetEvents, active, disabled })
        }
        isOpen={isOpen}
        onOpen={this.handleOpen}
        onClose={this.handleClose}
        placement={placement}
        isFlipEnabled={false}
        zIndex={1}
      >
        <div className="cr-date-picker cr-flex cr-flex-col">
          <DateRangePickerForm
            single={single}
            startDate={startDate}
            endDate={endDate}
            focusedInput={focusedInput}
            dateFormat={dateFormat}
            availableStartDate={availableStartDate}
            availableEndDate={availableEndDate}
            numberOfMonths={numberOfMonths}
            hasPresets={!!presetDates}
            offset={offset}
            customValidation={customValidation}
            onStartDateFocus={this.handleStartDateFocus}
            onStartDateBlur={this.handleStartDateBlur}
            onEndDateFocus={this.handleEndDateFocus}
            onEndDateBlur={this.handleEndDateBlur}
            onSubmit={this.handleSubmit}
          />
          <div className="cr-date-picker__selection cr-flex cr-flex-row">
            {presetDates && (
              <DateRangePickerPresets
                presetDates={presetDates}
                startDate={startDate}
                endDate={endDate}
                availableStartDate={availableStartDate}
                availableEndDate={availableEndDate}
                onDatesChange={this.handleDatesChange}
              />
            )}
            <div className="cr-date-picker__calendar cr-flex-1">
              <DayPickerRangeController
                key={key}
                startDate={startDate}
                endDate={endDate}
                focusedInput={focusedInput}
                onDatesChange={({ startDate: newStartDate, endDate: newEndDate }) =>
                  this.handleDatesChange({
                    startDate: newStartDate,
                    endDate: newEndDate,
                    focusedInput: single ? START_DATE : END_DATE,
                  })
                }
                navPrev={ARROW_LEFT}
                navNext={ARROW_RIGHT}
                numberOfMonths={numberOfMonths}
                hideKeyboardShortcutsPanel
                keepOpenOnDateSelect
                minimumNights={MINIMUM_NIGHTS}
                renderMonthElement={this.renderMonthElement}
                renderDayContents={this.renderDayContents}
                isOutsideRange={currentDay =>
                  isOutsideRange(currentDay, availableStartDate, availableEndDate, offset)
                }
                transitionDuration={TRANSITION_DURATION}
                initialVisibleMonth={
                  initialVisibleMonth && (() => initialVisibleMonth({ startDate, endDate }))
                }
                weekDayFormat={CALENDAR_WEEKDAY_FORMAT}
                {...rest}
              />
            </div>
          </div>
        </div>
      </Popover>
    ) : (
      <Spinner size="1rem" color="gray-300" />
    );
  }
}

DateRangePicker.propTypes = {
  dateFormat: PropTypes.string,
  startDate: PropTypes.shape({
    _isAMomentObject: PropTypes.bool,
  }).isRequired,
  endDate: PropTypes.shape({
    _isAMomentObject: PropTypes.bool,
  }).isRequired,
  availableStartDate: PropTypes.shape({
    _isAMomentObject: PropTypes.bool,
  }),
  availableEndDate: PropTypes.shape({
    _isAMomentObject: PropTypes.bool,
  }),
  onChange: PropTypes.func.isRequired,
  presetDates: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      divider: PropTypes.bool,
      startDate: PropTypes.shape({
        _isAMomentObject: PropTypes.bool,
      }),
      endDate: PropTypes.shape({
        _isAMomentObject: PropTypes.bool,
      }),
    })
  ),
  numberOfMonths: PropTypes.number,
  single: PropTypes.bool,
  placement: PropTypes.oneOf([
    'top',
    'top-start',
    'top-end',
    'bottom',
    'bottom-start',
    'bottom-end',
    'left',
    'right',
  ]),
  disabled: PropTypes.bool,
  offset: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  customValidation: PropTypes.func,
};

DateRangePicker.defaultProps = {
  dateFormat: DEFAULT_DATE_FORMAT,
  single: false,
  placement: 'bottom-start',
  numberOfMonths: VISIBLE_MONTHS,
  disabled: false,
};

export default DateRangePicker;

export { START_DATE, END_DATE, CALENDAR_MONTH_FORMAT, DatePicker };
